import { AfterViewInit, Directive, ElementRef, inject, Input, NgZone, Renderer2 } from '@angular/core';
import { LoggerService } from '@lx/shared/logger';

@Directive({
  selector: '[lxDragScroll]',
  standalone: true,
})
export class DragScrollDirective implements AfterViewInit {
  @Input('lxDragScroll') targetClass: string | undefined; // Input property to receive the target element's class name to be scrollable by dragging

  private mouseDown = false;
  private startX = 0;
  private scrollLeft = 0;
  private scrollEl: HTMLElement | undefined;
  private moved = false;

  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly zone = inject(NgZone);
  private readonly logger = inject(LoggerService);

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      const container = this.el.nativeElement as HTMLElement;
      const scrollEl = container.querySelector(`.${this.targetClass}`) as HTMLElement;

      if (!scrollEl) {
        this.logger.warn(`appDragScroll: element .${this.targetClass} not found`);
        return;
      }

      this.scrollEl = scrollEl;
      this.initScrollListeners(scrollEl);
    });
  }

  private initScrollListeners(el: HTMLElement): void {
    this.renderer.setStyle(el, 'cursor', 'grab');
    this.renderer.setStyle(el, 'user-select', 'none');

    el.addEventListener('mousedown', this.onMouseDown);
    el.addEventListener('mouseleave', this.onMouseLeave);
    el.addEventListener('mouseup', this.onMouseUp);
    el.addEventListener('mousemove', this.onMouseMove);
    el.addEventListener('click', this.onClick, true);
  }

  private onMouseDown = (e: MouseEvent) => {
    this.mouseDown = true;
    this.moved = false;

    // Save the initial horizontal mouse position relative to the scroll container
    this.startX = e.pageX - this.scrollEl.offsetLeft;

    // Store the scroll position at the start of the drag
    this.scrollLeft = this.scrollEl.scrollLeft;
  };

  private onMouseLeave = () => {
    this.mouseDown = false;
  };

  private onMouseUp = () => {
    this.mouseDown = false;
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.mouseDown) {
      return;
    }

    e.preventDefault();

    // Current mouse position relative to the scroll container
    const x = e.pageX - this.scrollEl.offsetLeft;

    // Calculate how much the mouse has moved since the drag started
    // This determines how far we should scroll
    const walk = x - this.startX;

    // If the movement is significant, mark as "moved" to cancel clicks later
    if (Math.abs(walk) > 5) {
      this.moved = true;
    }

    // Apply the scroll: move content in the opposite direction of the mouse drag
    this.scrollEl.scrollLeft = this.scrollLeft - walk;
  };

  private onClick = (e: MouseEvent) => {
    // Prevent accidental click events after dragging
    if (this.moved) {
      e.stopImmediatePropagation();
      e.preventDefault();
      this.moved = false;
    }
  };
}
